import { useState, useCallback, useRef, useEffect } from 'react';
import superjson from 'superjson';
import { stopAllKittenTTS } from './useKittenTTS';
import { stopAllInworldTTS } from './useInworldTTS';

// Voxtral (Mistral) TTS voices
export const VOXTRAL_VOICES = [
  { id: 'jessica', name: 'Jessica', gender: 'female', style: 'warm' },
  { id: 'emma', name: 'Emma', gender: 'female', style: 'professional' },
  { id: 'james', name: 'James', gender: 'male', style: 'authoritative' },
  { id: 'michael', name: 'Michael', gender: 'male', style: 'friendly' },
  { id: 'sophia', name: 'Sophia', gender: 'female', style: 'gentle' },
  { id: 'oliver', name: 'Oliver', gender: 'male', style: 'casual' },
  { id: 'aria', name: 'Aria', gender: 'female', style: 'energetic' },
  { id: 'daniel', name: 'Daniel', gender: 'male', style: 'deep' },
] as const;

export type VoxtralVoiceId = (typeof VOXTRAL_VOICES)[number]['id'];

interface VoxtralTTSOptions {
  voice?: VoxtralVoiceId;
  speed?: number;
}

interface UseVoxtralTTSReturn {
  speak: (text: string, options?: VoxtralTTSOptions) => Promise<void>;
  stop: () => void;
  isPlaying: boolean;
  isSpeaking: boolean;
  error: string | null;
}

// Global reference to track the active Voxtral TTS instance
let activeVoxtralTTSInstance: { stop: () => void } | null = null;

/**
 * Stop any globally active Voxtral TTS instance
 */
export function stopAllVoxtralTTS() {
  if (activeVoxtralTTSInstance) {
    activeVoxtralTTSInstance.stop();
    activeVoxtralTTSInstance = null;
  }
}

// Global cooldown after API errors
let voxtralErrorCooldownUntil = 0;
const VOXTRAL_ERROR_COOLDOWN_MS = 30_000;
const VOXTRAL_UNAVAILABLE_COOLDOWN_MS = 5 * 60_000; // 5 min if server not configured
let voxtralInFlight = false;

export function useVoxtralTTS(): UseVoxtralTTSReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
      } catch {
        // Ignore if already stopped
      }
      sourceNodeRef.current = null;
    }

    setIsPlaying(false);
    setIsSpeaking(false);

    if (activeVoxtralTTSInstance?.stop === stop) {
      activeVoxtralTTSInstance = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const speak = useCallback(
    async (text: string, options: VoxtralTTSOptions = {}) => {
      // Skip if in cooldown
      if (Date.now() < voxtralErrorCooldownUntil) return;

      // Prevent concurrent calls
      if (voxtralInFlight) return;
      voxtralInFlight = true;

      // Stop any existing TTS playback (all providers)
      stopAllVoxtralTTS();
      stopAllKittenTTS();
      stopAllInworldTTS();

      if (!text.trim()) {
        voxtralInFlight = false;
        return;
      }

      setError(null);
      setIsSpeaking(true);

      const { voice = 'jessica', speed = 1.0 } = options;

      try {
        abortControllerRef.current = new AbortController();

        const response = await fetch('/_api/tts/voxtral', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: superjson.stringify({ text, voiceId: voice, speed }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const statusCode = response.status;
          if (statusCode === 503) {
            // Server not configured — long cooldown
            voxtralErrorCooldownUntil = Date.now() + VOXTRAL_UNAVAILABLE_COOLDOWN_MS;
            console.warn('[VoxtralTTS] Not configured — disabled for 5 minutes');
          } else {
            voxtralErrorCooldownUntil = Date.now() + VOXTRAL_ERROR_COOLDOWN_MS;
          }
          voxtralInFlight = false;
          setError(`Voxtral TTS failed: ${statusCode}`);
          setIsSpeaking(false);
          return;
        }

        const audioData = await response.arrayBuffer();

        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        const audioContext = audioContextRef.current;

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const audioBuffer = await audioContext.decodeAudioData(audioData);

        const sourceNode = audioContext.createBufferSource();
        sourceNode.buffer = audioBuffer;
        sourceNode.connect(audioContext.destination);

        sourceNode.onended = () => {
          setIsPlaying(false);
          setIsSpeaking(false);
          sourceNodeRef.current = null;
          if (activeVoxtralTTSInstance?.stop === stop) {
            activeVoxtralTTSInstance = null;
          }
        };

        activeVoxtralTTSInstance = { stop };

        sourceNodeRef.current = sourceNode;
        setIsPlaying(true);
        sourceNode.start();
        voxtralInFlight = false;
      } catch (err) {
        voxtralInFlight = false;
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('[VoxtralTTS] Error:', err);
        voxtralErrorCooldownUntil = Date.now() + VOXTRAL_ERROR_COOLDOWN_MS;
        setError(err instanceof Error ? err.message : 'TTS failed');
        setIsSpeaking(false);
      }
    },
    [stop],
  );

  return {
    speak,
    stop,
    isPlaying,
    isSpeaking,
    error,
  };
}

/**
 * Get a consistent Voxtral voice for a persona based on stable hash
 */
export function getVoxtralVoiceForPersona(
  personaId: number | string,
  personaName?: string,
): VoxtralVoiceId {
  let hash = 0;
  const str = String(personaId || personaName || 'default');
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  const voiceIndex = Math.abs(hash) % VOXTRAL_VOICES.length;
  return VOXTRAL_VOICES[voiceIndex].id;
}
